
import React from 'react';
import { SimulationConfig } from './types';
import { CollapsibleSection } from './CollapsibleSection';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';

interface RhythmicEscapeSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const RhythmicEscapeSettingsSection: React.FC<RhythmicEscapeSettingsSectionProps> = ({ config, setConfig }) => {
  const darkMode = config.darkMode;
  
  return (
    <CollapsibleSection title="Rhythmic Escape Hybrid" icon="fa-shuffle" darkMode={config.darkMode}>
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 mb-2">
            <p className="text-[10px] text-orange-400 font-medium leading-relaxed">
                <i className="fa-solid fa-circle-info mr-1.5 opacity-70"></i>
                This mode combines Escape rings with Rhythmic movement. The ball follows the song path but interacts with breakable rings.
            </p>
        </div>

        <ControlGroup label="Safety Shield Ring" tooltip="Adds an inner ring without gaps to prevent the ball from escaping too early." modes={['RHYTHMIC_ESCAPE']}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase opacity-60">Enable Shield</span>
                <Toggle active={config.rhythmicEscapeShieldRing} onClick={() => setConfig(p => ({...p, rhythmicEscapeShieldRing: !p.rhythmicEscapeShieldRing}))} color="bg-orange-500" />
            </div>
        </ControlGroup>

        {config.rhythmicEscapeShieldRing && (
            <div className="pl-4 border-l-2 border-orange-500/20 space-y-4 pt-2">
                <SmartInput 
                    label="Shield Radius" 
                    min={20} max={400} 
                    value={config.rhythmicEscapeShieldRingRadius} 
                    onChange={(v: number) => setConfig(p => ({...p, rhythmicEscapeShieldRingRadius: v}))} 
                    darkMode={config.darkMode} 
                />
                
                <ControlGroup label="Destroy Mode" tooltip="How the shield should be deactivated.">
                    <select 
                        value={config.rhythmicEscapeShieldRingDestroyMode} 
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyMode: e.target.value as any}))}
                        className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        <option value="BOUNCES">By Bounce Count</option>
                        <option value="TIME">By Timer (Seconds)</option>
                    </select>
                </ControlGroup>

                <SmartInput 
                    label={config.rhythmicEscapeShieldRingDestroyMode === 'BOUNCES' ? 'Bounce Count' : 'Timer (s)'} 
                    min={1} max={100} 
                    value={config.rhythmicEscapeShieldRingDestroyValue} 
                    onChange={(v: number) => setConfig(p => ({...p, rhythmicEscapeShieldRingDestroyValue: v}))} 
                    darkMode={config.darkMode} 
                />
            </div>
        )}

        <div className="pt-2 border-t border-white/5">
             <ControlGroup label="Auto Sync Movement" tooltip="Ensures the ball always hits the rings precisely on beat.">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase opacity-60">Auto Sync</span>
                    <Toggle active={config.rhythmicEscapeAutoSync} onClick={() => setConfig(p => ({...p, rhythmicEscapeAutoSync: !p.rhythmicEscapeAutoSync}))} />
                </div>
            </ControlGroup>
            <p className="text-[9px] opacity-40 mt-1 pl-1">
                Synchronizes ball velocity with song rhythm.
            </p>
        </div>
      </div>
    </CollapsibleSection>
  );
};
